import { css } from '@microsoft/fast-element';
import { neutralForegroundActive, neutralForegroundRest, neutralInputFillHover, neutralInputFillRest, neutralLayer1, neutralStealthFillActive, neutralStealthFillHover, neutralStealthFillRest, neutralStrokeFocus, neutralStrokeRest, getToken } from '../../design-tokens';
import { appearanceBehavior } from '../../styles/behaviors';
import { elevationRule } from '../../styles/elevation';
import { heightNumber, paddingNumber } from '../../styles/size';
import { display } from '../../foundation/display';

export const comboboxStealthStyles = css`
        :host .control {
            background: ${neutralStealthFillRest};
            border-color: transparent;
            box-shadow: none;
        }

        :host(:not([disabled]):hover) .control {
            background: ${neutralStealthFillHover};
            border-color: transparent;
            box-shadow: none;
        }

        :host(:not([disabled]):active) .control {
            background: ${neutralStealthFillActive};
            border-color: transparent;
            box-shadow: none;
        }
    `;

export const comboboxLightweightStyles = css`
        :host .control {
            background: transparent;
            border-color: transparent;
            box-shadow: none;
        }

        :host(:not([disabled]):hover) .control {
            background: ${neutralStealthFillHover};
            border-color: transparent;
            box-shadow: none;
        }

        :host(:not([disabled]):active) .control {
            background: ${neutralStealthFillActive};
            border-color: transparent;
            box-shadow: none;
        }
    `;

export const comboboxStyles = css`
    ${display("inline-flex")} :host {
        --elevation: ${getToken('space-high-elevation')};
        --listbox-max-height: 6;
        --listbox-available-height: 100px;
        position: relative;
        box-sizing: border-box;

        font-family: ${getToken('typography-font-family')};
        fill: currentcolor;

        user-select: none;
        outline: none;
    }

    :host .listbox {
        ${elevationRule}
        background: ${neutralLayer1};
        border: calc(${getToken('space-stroke-width')} * 1px) solid ${neutralStrokeRest};
        border-radius: calc(${getToken('space-input-corner-radius')} * 1px);
        box-sizing: border-box;
        display: inline-flex;
        flex-direction: column;
        max-height: min(var(--listbox-available-height), calc((var(--listbox-max-height) * ${heightNumber} + ${getToken('space-stroke-width')}) * 1px));
        padding: 0;
        overflow-y: auto;
        z-index: 10;
    }

    :host([no-collapse]) .listbox {
        width: 100%;
        height: calc((var(--listbox-max-height) * ${heightNumber} + ${getToken('space-stroke-width')}) * 1px);
        max-height: none;
        position: static !important;
        transform: none !important;
        top: auto !important;
        left: auto !important;
    }

    :host .listbox[hidden] {
        display: none;
    }

    :host .control {
        border: calc(${getToken('space-stroke-width')} * 1px) solid ${neutralStrokeRest};
        border-radius: calc(${getToken('space-input-corner-radius')} * 1px);
        height: calc(${heightNumber} * 1px);
        vertical-align: top;
        background: ${neutralInputFillRest};
        color: ${neutralForegroundRest};
        box-shadow: 0 0 calc(${getToken('space-input-shadow')} * 1px) 0 rgba(0, 0, 0, 0.1);
        align-items: center;
        box-sizing: border-box;
        cursor: pointer;
        display: flex;
        font-family: inherit;
        font-size: inherit;
        line-height: inherit;
        padding-left: 1ch;
        width: 100%;
    }

    :host(:not([disabled]):hover) .control {
        background: ${neutralInputFillHover};
        border-color: ${getToken('color-primary-hover')};
    }

    :host(:focus-visible) .control {
        border-color: ${neutralStrokeFocus};
        box-shadow: 0 0 0 calc(${getToken('space-focus-stroke-width')} * 1px) ${neutralStrokeFocus};
    }

    :host([disabled]) {
        cursor: not-allowed;
        opacity: ${getToken('color-disabled-opacity')};
    }

    :host([disabled]) .control {
        cursor: not-allowed;
        user-select: none;
    }

    :host([open]) .control {
        color: ${neutralForegroundActive};
    }

    :host([open][position="above"]) .listbox {
        bottom: calc((${heightNumber} + ${getToken('space-design-unit')}) * 1px);
    }

    :host([open][position="below"]) .listbox {
        top: calc((${heightNumber} + ${getToken('space-design-unit')}) * 1px);
    }

    .selected-value {
        -webkit-appearance: none;
        background: transparent;
        border: none;
        color: inherit;
        font-size: ${getToken('typography-control-font-size')};
        line-height: ${getToken('typography-control-line-height')};
        height: calc(100% - (${getToken('space-stroke-width')} * 1px));
        margin: auto 0;
        width: 100%;
    }

    .selected-value:hover,
    .selected-value:focus-visible,
    .selected-value:disabled,
    .selected-value:active {
        outline: none;
    }

    .indicator {
        display: flex;
        justify-content: center;
        flex: 0 0 auto;
        width: calc(${heightNumber} * 1px);
    }

    slot[name="listbox"] {
        display: none;
        width: 100%;
    }

    :host([open]) slot[name="listbox"] {
        display: flex;
        position: absolute;
        ${elevationRule}
    }

    :host(.error) {
        border-color: ${getToken('color-error')} !important;
        border-width: calc(${getToken('space-focus-stroke-width')} * 1px) !important;
    }

    .end {
        margin-inline-start: auto;
    }

    ::slotted(svg),
    slot > svg  {
        display: flex;
        height: 1em;
        min-height: calc(${getToken('space-design-unit')} * 4px);
        min-width: calc(${getToken('space-design-unit')} * 4px);
        width: 1em;
    }

    ::slotted([role="option"]) {
        flex: 0 0 auto;
    }

    ::slotted([role="option"]:not(:last-child)) {
        border-bottom: calc(${getToken('space-stroke-width')} * 1px) solid ${neutralStrokeRest} !important;
    }
`.withBehaviors(
    appearanceBehavior("stealth", comboboxStealthStyles),
    appearanceBehavior("lightweight", comboboxLightweightStyles)
);
